import React, { useState, useEffect, useRef } from 'react';
import { Edit, Delete } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { IconEyeEdit } from '@tabler/icons-react';
import { FileText, Upload, User, Calendar, FileType, Search, AlertCircle, CheckCircle, Loader2, X } from 'lucide-react';
import { createDocument, deleteDocument } from '../services/documentAPIs';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Divider } from '@mui/material';

function Documents({ selectedDoc, onDocSelect, setSelectedDoc, employees, setEmployees, documents, setDocuments, loading, setLoading, fetchDocuments }) {
  console.log('Documents component rendered', documents);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState('All');
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    type: 'Contract',
    description: '',
    file: null,
    // employeeId REMOVED — will use selectedEmployee from state
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);

  // For searchable dropdown
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError(null);
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Filter documents by selected employee, type, and search term
  const filteredDocuments = documents.filter(doc => {
    const matchesEmployee = selectedEmployee ? doc.employee?.employeeId === selectedEmployee.employeeId : true;
    const matchesType = selectedDocumentType === 'All' ? true : doc.type === selectedDocumentType;
    const matchesSearch = searchTerm === '' ||
      (doc.description?.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesEmployee && matchesType && matchesSearch;
  });

  // Document counts by type
  const documentCounts = documents.reduce((acc, doc) => {
    acc[doc.type] = (acc[doc.type] || 0) + 1;
    return acc;
  }, {});

  // Recent documents (last 10)
  const recentDocuments = [...documents]
    .sort((a, b) => new Date(b.uploadDate) - new Date(a.uploadDate))
    .slice(0, 10);

  // Handle upload form changes
  const handleUploadChange = (e) => {
    const { name, value, files } = e.target;
    setUploadForm(prev => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  // Handle document upload
  const handleUploadSubmit = async () => {
    if (!selectedEmployee) {
      setError('Please select an employee first.');
      return;
    }
    if (!uploadForm.description || !uploadForm.file) {
      setError('Please fill all required fields and select a file.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      const documentData = {
        employee: { employeeId: selectedEmployee.employeeId }, // ✅ Uses selectedEmployee
        type: uploadForm.type,
        description: uploadForm.description,
      };
      formData.append('document', new Blob([JSON.stringify(documentData)], { type: 'application/json' }));
      formData.append('file', uploadForm.file);

      const newDocument = await createDocument(formData);
      setDocuments([...documents, newDocument]);
      setSuccess('Document uploaded successfully!');
      setUploadForm({ type: 'Contract', description: '', file: null }); // ✅ employeeId removed
      setShowUploadForm(false);
    } catch (err) {
      setError('Failed to upload document. Please try again.');
      console.error('Error uploading document:', err);
    } finally {
      setUploading(false);
    }
  };

  // Handle document deletion
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteDocument(deleteId);
      setDocuments(documents.filter(doc => doc.id !== deleteId));
      setSuccess('Document deleted successfully!');
      fetchDocuments();
      setOpen(false);
    } catch (err) {
      setError('Failed to delete document. Please try again.');
      console.error('Error deleting document:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="doc-docs-container">
        <div className="doc-loading-container">
          <Loader2 className="doc-loader-icon" />
          <p className="doc-loading-text">Loading documents...</p>
        </div>
      </div>
    );
  }

  // Filter employees for searchable dropdown
  const filteredEmployeesForSearch = employees.filter(emp =>
    `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="doc-docs-container">
      <div className="doc-content-wrapper">

        {/* Controls Row — NOW INCLUDES ACTION BAR INSIDE */}
        <div className="doc-controls">

          {/* Select Employee (Searchable) */}
          <div className="doc-control-box" ref={dropdownRef}>
            <div className="doc-control-header">
              <User className="doc-control-icon" />
              <h2 className="doc-control-title">Select Employee</h2>
            </div>
            <div className="doc-searchable-select">
              <input
                type="text"
                placeholder="Search employee..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsDropdownOpen(true)}
                className="doc-search-input"
                onClick={() => setIsDropdownOpen(true)}
              />
              {isDropdownOpen && (
                <div className="doc-dropdown-list">
                  <div
                    className="doc-dropdown-item"
                    onClick={() => {
                      setSelectedEmployee(null);
                      setSearchQuery('');
                      setIsDropdownOpen(false);
                    }}
                  >
                    All Employees
                  </div>
                  {filteredEmployeesForSearch.map(emp => (
                    <div
                      key={emp.employeeId}
                      className="doc-dropdown-item"
                      onClick={() => {
                        setSelectedEmployee(emp);
                        setSearchQuery(`${emp.firstName} ${emp.lastName}`);
                        setIsDropdownOpen(false);
                      }}
                    >
                      {emp.firstName} {emp.lastName}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Document Type Selector */}
          <div className="doc-control-box">
            <div className="doc-control-header">
              <FileType className="doc-control-icon" />
              <h2 className="doc-control-title">Document Type</h2>
            </div>
            <select
              value={selectedDocumentType}
              onChange={(e) => setSelectedDocumentType(e.target.value)}
              className="doc-select-input"
            >
              <option value="All">All</option>
              <option value="Contract">Contract</option>
              <option value="National ID">National ID</option>
              <option value="Diploma">Diploma</option>
              <option value="Payroll">Payroll</option>
              <option value="Personal Documents">Personal Documents</option>
              <option value="Code of Conduct">Code of Conduct</option>
              <option value="Probation Period Agreement">Probation Period Agreement</option>
              <option value="Bank Details Form">Bank Details Form</option>
              <option value="Promotion Letters">Promotion Letters</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* ✅ MOVED HERE: Action Bar (Showing count + Upload Button) */}
          <div className="doc-action-bar doc-action-bar-inline">
            <div className="doc-action-info">
              Showing {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
              {selectedEmployee && ` for ${selectedEmployee.firstName} ${selectedEmployee.lastName}`}
              {selectedDocumentType !== 'All' && ` (${selectedDocumentType})`}
            </div>
            <button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="doc-action-button"
            >
              <Upload className="doc-button-icon" />
              Upload Document
            </button>
          </div>

        </div>

        {/* Notifications */}
        {error && (
          <div className="doc-notification doc-error">
            <AlertCircle className="doc-notification-icon doc-error-icon" />
            <span className="doc-notification-text">{error}</span>
            <button onClick={() => setError(null)} className="doc-notification-close">
              <X className="doc-close-icon" />
            </button>
          </div>
        )}

        {success && (
          <div className="doc-notification doc-success">
            <CheckCircle className="doc-notification-icon doc-success-icon" />
            <span className="doc-notification-text">{success}</span>
            <button onClick={() => setSuccess(null)} className="doc-notification-close">
              <X className="doc-close-icon" />
            </button>
          </div>
        )}

        {/* Upload Form — EMPLOYEE SELECTOR REMOVED */}
        {showUploadForm && (
          <div className="doc-upload-form-box">
            <h2 className="doc-upload-title">
              <Upload className="doc-upload-icon" />
              Upload New Document
            </h2>
            <div className="doc-upload-form">
              <div className="doc-form-grid">
                {/* ✅ EMPLOYEE FIELD REMOVED — auto-bound to selectedEmployee */}
                <div className="doc-form-group">
                  <label className="doc-form-label" htmlFor="type-select">Document Type *</label>
                  <select
                    id="type-select"
                    name="type"
                    value={uploadForm.type}
                    onChange={handleUploadChange}
                    className="form-doc-select-input"
                    aria-required="true"
                  >
                    <option value="Contract">Contract</option>
                    <option value="National ID">National ID</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Payroll">Payroll</option>
                    <option value="Personal Documents">Personal Documents</option>
                    <option value="Code of Conduct">Code of Conduct</option>
                    <option value="Probation Period Agreement">Probation Period Agreement</option>
                    <option value="Bank Details Form">Bank Details Form</option>
                    <option value="Promotion Letters">Promotion Letters</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="doc-form-group">
                  <label className="doc-form-label" htmlFor="description-input">Description *</label>
                  <input
                    id="description-input"
                    type="text"
                    name="description"
                    value={uploadForm.description}
                    onChange={handleUploadChange}
                    className="doc-text-input"
                    placeholder="Enter document description"
                    aria-required="true"
                  />
                </div>
                <div className="doc-form-group">
                  <label className="doc-form-label" htmlFor="file-upload">File *</label>
                  <input
                    id="file-upload"
                    type="file"
                    name="file"
                    onChange={handleUploadChange}
                    className="doc-file-input"
                    accept=".pdf,.doc,.docx,.txt"
                    aria-label="Upload a document file"
                    aria-required="true"
                  />
                </div>
              </div>
              <div className="doc-form-actions">
                <button
                  onClick={handleUploadSubmit}
                  disabled={uploading}
                  className="doc-submit-button"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="doc-button-icon doc-loader" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <FileText className="doc-button-icon" />
                      Upload Document
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowUploadForm(false);
                    setUploadForm({ type: 'Contract', description: '', file: null });
                  }}
                  className="doc-cancel-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="doc-main-content">
          <div className="doc-documents-list">
            <div className="doc-list-header">
              <h2 className="doc-list-title">
                <FileText className="doc-list-icon" />
                {selectedEmployee
                  ? `${selectedEmployee.firstName} ${selectedEmployee.lastName}'s ${selectedDocumentType === 'All' ? 'Documents' : selectedDocumentType}`
                  : selectedDocumentType === 'All' ? 'All Documents' : `${selectedDocumentType} Documents`}
              </h2>
            </div>
            <div className="doc-list-content">
              {filteredDocuments.length > 0 ? (
                <div className="doc-documents-container">
                  {filteredDocuments.map(doc => (
                    <div key={doc.id} className="doc-document-item">
                      <div className="doc-document-details">
                        <div style={{ display: 'flex', flexDirection: 'row' }}>
                          <h3 className="doc-document-title">{doc.description}</h3>
                          <div className="doc-document-type">
                            <span className="doc-type-badge">{doc.type}</span>
                          </div>
                        </div>
                        <div className="doc-document-meta">
                          <User className="doc-meta-icon" />
                          <span className="doc-meta-text">{doc.employee?.firstName} {doc.employee?.lastName}</span>
                          <Calendar className="doc-meta-icon" />
                          <span className="doc-meta-text">{formatDate(doc.uploadDate)}</span>
                          <Box sx={{ ml: 'auto', mb: 0 }}>
                            <IconButton
                              onClick={() => setSelectedDoc(doc)}
                              style={{
                                color: '#1976d2',
                                '&:hover': { color: '#115293' },
                              }}
                              aria-label="view document"
                            >
                              <IconEyeEdit size={25} />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setOpen(true);
                                setDeleteId(doc.id);
                              }}
                              sx={{
                                color: '#f44336',
                                '&:hover': { color: '#d32f2f' },
                              }}
                              aria-label="delete document"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="doc-no-documents">
                  <FileText className="doc-no-documents-icon" />
                  <p className="doc-no-documents-text">
                    No {selectedDocumentType === 'All' ? '' : selectedDocumentType.toLowerCase()} documents found
                    {selectedEmployee ? ` for ${selectedEmployee.firstName} ${selectedEmployee.lastName}` : ''}.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Delete dialog */}
          <Dialog open={open} onClose={() => setOpen(false)}>
            <DialogTitle>Confirm Delete</DialogTitle>
            {error && (
              <div className="doc-notification doc-error">
                <AlertCircle className="doc-notification-icon doc-error-icon" />
                <span className="doc-notification-text">{error}</span>
                <button onClick={() => setError(null)} className="doc-notification-close">
                  <X className="doc-close-icon" />
                </button>
              </div>
            )}
            {success && (
              <div className="doc-notification doc-success">
                <CheckCircle className="doc-notification-icon doc-success-icon" />
                <span className="doc-notification-text">{success}</span>
                <button onClick={() => setSuccess(null)} className="doc-notification-close">
                  <X className="doc-close-icon" />
                </button>
              </div>
            )}
            <DialogContent>Are you sure you want to delete the document?</DialogContent>
            <DialogActions>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleDelete} color="error">Delete</Button>
            </DialogActions>
          </Dialog>

          {/* Document Sidebar Overview */}
          <div className="doc-doc-sidebar">
            <div className="doc-overview-box">
              <h2 className="doc-overview-title">Document Overview</h2>
              <div className="doc-overview-content">
                <div className="doc-overview-item">
                  <span className="doc-overview-label">Contracts</span>
                  <span className="doc-overview-value doc-contract">{documentCounts['Contract'] || 0}</span>
                </div>
                <div className="doc-overview-item">
                  <span className="doc-overview-label">Payroll</span>
                  <span className="doc-overview-value doc-payroll">{documentCounts['Payroll'] || 0}</span>
                </div>
                <div className="doc-overview-item">
                  <span className="doc-overview-label">Personal Documents</span>
                  <span className="doc-overview-value doc-personal">{documentCounts['Personal Documents'] || 0}</span>
                </div>
              </div>
            </div>

            {/* Recent Uploads */}
            <div className="doc-recent-box">
              <h2 className="doc-recent-title">Recent Uploads</h2>
              <div className="doc-recent-content">
                {recentDocuments.map(doc => (
                  <div key={doc.documentId} className="doc-recent-item">
                    <p className="doc-recent-title-text">{doc.description}</p>
                    <p className="doc-recent-meta">{doc.employee?.firstName} {doc.employee?.lastName}</p>
                    <p className="doc-recent-meta">{formatDate(doc.uploadDate)}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Documents;